import React, { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { DEFAULT_SOUND_ID, SOUND_PRESETS } from '../utils/soundPresets';
import { DEFAULT_CHAT_SOUND_ID, CHAT_SOUND_PRESETS } from '../utils/chatSoundPresets';
import i18n from '../i18n';

const STORAGE_KEY = 'notesplus_preferences_v1';
// Prior-install signals (renderer). Checked ONLY at first SettingsProvider init —
// before the persist effect writes preferences_v1 (which would fake "existing").
const AUTH_TOKEN_KEY = 'notesplus_auth_token_v1';
const AUTH_USER_KEY = 'notesplus_auth_user_v1';
const LANGUAGE_KEY = 'notesplus_language';
const VALID_SOUND_IDS = new Set(SOUND_PRESETS.map((preset) => preset.id));
const normalizeSoundId = (value) => (VALID_SOUND_IDS.has(value) ? value : DEFAULT_SOUND_ID);

const VALID_CHAT_SOUND_IDS = new Set(CHAT_SOUND_PRESETS.map((preset) => preset.id));
const normalizeChatSoundId = (value) => (VALID_CHAT_SOUND_IDS.has(value) ? value : DEFAULT_CHAT_SOUND_ID);

/**
 * Decide first-run vs existing-user migration BEFORE preferences persist.
 * - Clean install: no prior LS → applyDefaultOn=true
 * - Upgrade / existing: prefs or auth/language already present → migrate OS as-is
 * Do NOT treat missing launchOnStartupInitialized alone as clean install.
 * Do NOT use notesplus_theme — ThemeProvider writes it on every mount.
 */
const readLaunchBootstrapPlan = () => {
  if (typeof window === 'undefined') {
    return { needsInit: false, applyDefaultOn: false, isExistingUser: false };
  }

  let initialized = false;
  let hadPreferences = false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      hadPreferences = true;
      try {
        const parsed = JSON.parse(raw);
        initialized = parsed.launchOnStartupInitialized === true;
      } catch (_) {
        // Corrupt prefs still mean the app was used before
      }
    }
  } catch (_) {
    // ignore
  }

  if (initialized) {
    return { needsInit: false, applyDefaultOn: false, isExistingUser: true };
  }

  let isExistingUser = hadPreferences;
  if (!isExistingUser) {
    try {
      isExistingUser = !!(
        localStorage.getItem(AUTH_TOKEN_KEY) ||
        localStorage.getItem(AUTH_USER_KEY) ||
        localStorage.getItem(LANGUAGE_KEY)
      );
    } catch (_) {
      // ignore
    }
  }

  return {
    needsInit: true,
    // True only for genuine clean first run (no prior app state)
    applyDefaultOn: !isExistingUser,
    isExistingUser,
  };
};

const DEFAULT_PREFERENCES = {
  showBreakNotifications: true,
  showCalendarNotifications: true,
  playNotificationSound: true,
  notificationSoundId: DEFAULT_SOUND_ID,
  breakNotificationSoundId: DEFAULT_SOUND_ID,
  calendarNotificationSoundId: DEFAULT_SOUND_ID,
  chatNotificationSoundId: DEFAULT_CHAT_SOUND_ID,
  chatNotificationsMuted: false,
  // Group chat notifications (separate from direct chat)
  groupNotificationSoundId: DEFAULT_CHAT_SOUND_ID,
  groupNotificationsMuted: false,
  lastSelectedTheme: 'dark',
  // Display-only until OS sync. SoT = OS login item on packaged non-Store Electron.
  launchOnStartup: false,
  // Not SoT — only tracks whether first-run default ON bootstrap already ran.
  launchOnStartupInitialized: false,
};

const canUseLaunchOnStartupApi = () =>
  typeof window !== 'undefined' &&
  !!window.api?.supportsLaunchOnStartup &&
  typeof window.api.getLaunchOnStartup === 'function' &&
  typeof window.api.setLaunchOnStartup === 'function';

const parseOpenAtLogin = (result) => {
  if (typeof result === 'boolean') return result;
  if (result && typeof result.openAtLogin === 'boolean') return result.openAtLogin;
  return null;
};

const SettingsContext = createContext(null);

export const SettingsProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_PREFERENCES;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return DEFAULT_PREFERENCES;
      }
      const parsed = JSON.parse(stored);
      const merged = {
        ...DEFAULT_PREFERENCES,
        ...parsed,
        showBreakNotifications:
          typeof parsed.showBreakNotifications === 'boolean'
            ? parsed.showBreakNotifications
            : DEFAULT_PREFERENCES.showBreakNotifications,
        showCalendarNotifications:
          typeof parsed.showCalendarNotifications === 'boolean'
            ? parsed.showCalendarNotifications
            : DEFAULT_PREFERENCES.showCalendarNotifications,
      };
      merged.breakNotificationSoundId = normalizeSoundId(
        parsed.breakNotificationSoundId ||
          parsed.notificationSoundId ||
          DEFAULT_PREFERENCES.breakNotificationSoundId,
      );
      merged.calendarNotificationSoundId = normalizeSoundId(
        parsed.calendarNotificationSoundId ||
          parsed.notificationSoundId ||
          DEFAULT_PREFERENCES.calendarNotificationSoundId,
      );
      merged.notificationSoundId = normalizeSoundId(
        merged.notificationSoundId || merged.breakNotificationSoundId,
      );
      merged.chatNotificationSoundId = normalizeChatSoundId(
        parsed.chatNotificationSoundId || DEFAULT_PREFERENCES.chatNotificationSoundId,
      );
      merged.chatNotificationsMuted =
        typeof parsed.chatNotificationsMuted === 'boolean'
          ? parsed.chatNotificationsMuted
          : DEFAULT_PREFERENCES.chatNotificationsMuted;
      merged.groupNotificationSoundId = normalizeChatSoundId(
        parsed.groupNotificationSoundId || parsed.chatNotificationSoundId || DEFAULT_PREFERENCES.groupNotificationSoundId,
      );
      merged.groupNotificationsMuted =
        typeof parsed.groupNotificationsMuted === 'boolean'
          ? parsed.groupNotificationsMuted
          : DEFAULT_PREFERENCES.groupNotificationsMuted;
      const themeCandidate = parsed.lastSelectedTheme;
      merged.lastSelectedTheme =
        typeof themeCandidate === 'string' ? themeCandidate : DEFAULT_PREFERENCES.lastSelectedTheme;
      // Never trust localStorage for launchOnStartup display — OS is source of truth
      merged.launchOnStartup = DEFAULT_PREFERENCES.launchOnStartup;
      merged.launchOnStartupInitialized = parsed.launchOnStartupInitialized === true;
      // Dead Settings-only preference (Update Settings tab removed)
      delete merged.updateChannel;
      return merged;
    } catch (error) {
      console.error('Ayarlar yüklenirken hata oluştu:', error);
      return DEFAULT_PREFERENCES;
    }
  });
  const [activeTab, setActiveTab] = useState('user');
  const [launchOnStartupBusy, setLaunchOnStartupBusy] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
      } catch (error) {
        console.error('Ayarlar kaydedilirken hata:', error);
      }
    }
  }, [preferences]);

  // Packaged non-Store Electron only. Plan is frozen at first render (before persist
  // writes preferences_v1) so upgrades are not mistaken for clean installs.
  const launchBootstrapPlanRef = useRef(null);
  if (launchBootstrapPlanRef.current === null) {
    launchBootstrapPlanRef.current = readLaunchBootstrapPlan();
  }

  useEffect(() => {
    if (!canUseLaunchOnStartupApi()) return undefined;

    let cancelled = false;

    const syncFromOs = async ({ markInitialized } = {}) => {
      try {
        const result = await window.api.getLaunchOnStartup();
        if (cancelled) return;
        if (result && result.ok === false) return;
        const openAtLogin = parseOpenAtLogin(result);
        if (typeof openAtLogin !== 'boolean') return;
        setPreferences((prev) => {
          const nextInitialized = markInitialized || prev.launchOnStartupInitialized;
          if (prev.launchOnStartup === openAtLogin && prev.launchOnStartupInitialized === !!nextInitialized) {
            return prev;
          }
          return {
            ...prev,
            launchOnStartup: openAtLogin,
            launchOnStartupInitialized: nextInitialized ? true : prev.launchOnStartupInitialized,
          };
        });
      } catch (_) {
        // Keep display default; do not invent OS state
      }
    };

    const bootstrapAndSync = async () => {
      const plan = launchBootstrapPlanRef.current || {
        needsInit: false,
        applyDefaultOn: false,
        isExistingUser: false,
      };

      // Secondary signal: userData language-settings.json (safe async — not written on first paint).
      // Do NOT use theme-settings.json — ThemeProvider saves it on every mount.
      let isExistingUser = plan.isExistingUser === true;
      let applyDefaultOn = plan.applyDefaultOn === true;
      if (plan.needsInit && !isExistingUser && typeof window.api.hasPriorAppState === 'function') {
        try {
          const hint = await window.api.hasPriorAppState();
          if (hint && hint.prior === true) {
            isExistingUser = true;
            applyDefaultOn = false;
          }
        } catch (_) {
          // ignore — fall through with renderer-only plan
        }
      }

      if (plan.needsInit) {
        if (applyDefaultOn) {
          try {
            // Genuine clean NSIS first run only
            await window.api.setLaunchOnStartup(true);
          } catch (_) {
            // Still mark initialized so we never retry-force ON
          }
        }
        // Existing / upgrade: never setLoginItemSettings — preserve OS (incl. OFF)
        if (!cancelled) {
          setPreferences((prev) =>
            prev.launchOnStartupInitialized
              ? prev
              : { ...prev, launchOnStartupInitialized: true }
          );
        }
      }

      if (!cancelled) {
        await syncFromOs({ markInitialized: plan.needsInit });
      }
    };

    bootstrapAndSync();

    return () => {
      cancelled = true;
    };
  }, []);

  const updatePreferences = useCallback((partial) => {
    setPreferences((prev) => ({
      ...prev,
      ...partial,
    }));
  }, []);

  /**
   * User-driven toggle only. Writes OS login item, then re-reads and syncs UI.
   * On failure: leaves previous UI state and returns { ok: false, error }.
   */
  const setLaunchOnStartup = useCallback(async (enabled) => {
    if (!canUseLaunchOnStartupApi()) {
      return { ok: false, error: 'LAUNCH_ON_STARTUP_UNSUPPORTED' };
    }

    const wanted = !!enabled;
    let previous = false;
    setPreferences((prev) => {
      previous = !!prev.launchOnStartup;
      return { ...prev, launchOnStartup: wanted };
    });
    setLaunchOnStartupBusy(true);

    try {
      const setResult = await window.api.setLaunchOnStartup(wanted);
      if (!setResult || setResult.ok !== true) {
        setPreferences((prev) => ({ ...prev, launchOnStartup: previous }));
        return {
          ok: false,
          error: setResult?.error || 'LAUNCH_ON_STARTUP_SET_FAILED',
          openAtLogin: previous,
        };
      }

      const getResult = await window.api.getLaunchOnStartup();
      const verified = parseOpenAtLogin(getResult);
      if (typeof verified !== 'boolean' || verified !== wanted) {
        const rollback = typeof verified === 'boolean' ? verified : previous;
        setPreferences((prev) => ({ ...prev, launchOnStartup: rollback }));
        return {
          ok: false,
          error: 'LAUNCH_ON_STARTUP_VERIFY_FAILED',
          openAtLogin: rollback,
        };
      }

      setPreferences((prev) => ({
        ...prev,
        launchOnStartup: verified,
        launchOnStartupInitialized: true,
      }));
      return { ok: true, openAtLogin: verified };
    } catch (error) {
      setPreferences((prev) => ({ ...prev, launchOnStartup: previous }));
      return { ok: false, error: 'LAUNCH_ON_STARTUP_SET_FAILED' };
    } finally {
      setLaunchOnStartupBusy(false);
    }
  }, []);

  const setNotificationSoundId = useCallback(
    (soundId) => {
      if (!soundId) {
        return;
      }
      const normalized = normalizeSoundId(soundId);
      updatePreferences({
        notificationSoundId: normalized,
        breakNotificationSoundId: normalized,
        calendarNotificationSoundId: normalized,
      });
    },
    [updatePreferences],
  );

  const setBreakNotificationSoundId = useCallback(
    (soundId) => {
      if (!soundId) {
        return;
      }
      updatePreferences({ breakNotificationSoundId: normalizeSoundId(soundId) });
    },
    [updatePreferences],
  );

  const setCalendarNotificationSoundId = useCallback(
    (soundId) => {
      if (!soundId) {
        return;
      }
      updatePreferences({ calendarNotificationSoundId: normalizeSoundId(soundId) });
    },
    [updatePreferences],
  );

  const setChatNotificationSoundId = useCallback(
    (soundId) => {
      if (!soundId) {
        return;
      }
      updatePreferences({ chatNotificationSoundId: normalizeChatSoundId(soundId) });
    },
    [updatePreferences],
  );

  const setGroupNotificationSoundId = useCallback(
    (soundId) => {
      if (!soundId) {
        return;
      }
      updatePreferences({ groupNotificationSoundId: normalizeChatSoundId(soundId) });
    },
    [updatePreferences],
  );

  const value = useMemo(
    () => ({
      preferences,
      updatePreferences,
      setLaunchOnStartup,
      launchOnStartupBusy,
      setNotificationSoundId,
      setBreakNotificationSoundId,
      setCalendarNotificationSoundId,
      setChatNotificationSoundId,
      setGroupNotificationSoundId,
      activeTab,
      setActiveTab,
      resetPreferences: () =>
        setPreferences((prev) => ({
          ...DEFAULT_PREFERENCES,
          // Do not re-arm first-run bootstrap or invent OS state
          launchOnStartupInitialized: prev.launchOnStartupInitialized,
          launchOnStartup: prev.launchOnStartup,
        })),
    }),
    [
      preferences,
      updatePreferences,
      setLaunchOnStartup,
      launchOnStartupBusy,
      setNotificationSoundId,
      setBreakNotificationSoundId,
      setCalendarNotificationSoundId,
      setChatNotificationSoundId,
      setGroupNotificationSoundId,
      activeTab,
    ],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings yalnızca SettingsProvider içinde kullanılabilir.');
  }
  return context;
};

export const getLaunchOnStartupErrorMessage = (code) => {
  if (!code) return i18n.t('errors:LAUNCH_ON_STARTUP_SET_FAILED');
  const translated = i18n.t(`errors:${code}`, { defaultValue: '' });
  return translated || i18n.t('errors:LAUNCH_ON_STARTUP_SET_FAILED');
};
